/**
 * Component: Product carousel
 * Danish Modern L.A. Shopify Theme
 *
 * The track is a native scroll-snap container, so swipe, trackpad and keyboard
 * scrolling all work without this script. The component only adds:
 * - previous / next buttons that move one card at a time, optionally wrapping
 * - optional autoplay that pauses on hover, focus and reduced-motion
 * - hiding the buttons when every card already fits
 */

if (!customElements.get('product-carousel')) {
  class ProductCarousel extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-carousel-track]');
      if (!this.track) return;

      this.prevButton = this.querySelector('[data-carousel-prev]');
      this.nextButton = this.querySelector('[data-carousel-next]');
      this.autoplayDelay = parseInt(this.dataset.autoplaySpeed, 10) * 1000 || 6000;
      this.loop = this.dataset.loop !== 'false';
      this.autoplayEnabled =
        this.dataset.autoplay === 'true' && !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.timer = null;

      this.onPrev = () => this.step(-1, true);
      this.onNext = () => this.step(1, true);
      this.pause = () => this.stopAutoplay();
      // Only resume once the pointer has left AND focus has moved outside.
      this.resume = (event) => {
        if (this.matches(':hover')) return;
        const focused = event.type === 'focusout' ? event.relatedTarget : document.activeElement;
        if (focused && this.contains(focused)) return;
        this.startAutoplay();
      };
      this.onResize = () => this.updateControls();

      this.prevButton?.addEventListener('click', this.onPrev);
      this.nextButton?.addEventListener('click', this.onNext);
      this.addEventListener('mouseenter', this.pause);
      this.addEventListener('mouseleave', this.resume);
      this.addEventListener('focusin', this.pause);
      this.addEventListener('focusout', this.resume);

      this.resizeObserver = new ResizeObserver(this.onResize);
      this.resizeObserver.observe(this.track);
      this.track.addEventListener('scroll', this.onResize, { passive: true });

      this.updateControls();
      this.startAutoplay();
    }

    disconnectedCallback() {
      this.stopAutoplay();
      this.resizeObserver?.disconnect();
      this.prevButton?.removeEventListener('click', this.onPrev);
      this.nextButton?.removeEventListener('click', this.onNext);
      this.track?.removeEventListener('scroll', this.onResize);
    }

    get slides() {
      return Array.from(this.track.children);
    }

    /** Width of one card plus the gap after it. */
    get stepSize() {
      const [first, second] = this.slides;
      if (!first) return 0;
      if (!second) return first.getBoundingClientRect().width;
      return second.offsetLeft - first.offsetLeft;
    }

    get maxScroll() {
      return this.track.scrollWidth - this.track.clientWidth;
    }

    /** A couple of pixels of slack absorbs sub-pixel scroll positions. */
    get atStart() {
      return this.track.scrollLeft <= 2;
    }

    get atEnd() {
      return this.track.scrollLeft >= this.maxScroll - 2;
    }

    /**
     * Move one card in `direction`. With looping on, the track wraps round at
     * either end; with it off it simply stops and the button is disabled.
     */
    step(direction, fromUser = false) {
      const step = this.stepSize;
      if (!step) return;

      let target = this.track.scrollLeft + direction * step;

      if (direction > 0 && this.atEnd) {
        if (!this.loop) return;
        target = 0;
      }

      if (direction < 0 && this.atStart) {
        if (!this.loop) return;
        target = this.maxScroll;
      }

      this.track.scrollTo({ left: target, behavior: 'smooth' });

      if (fromUser) this.restartAutoplay();
    }

    updateControls() {
      const overflowing = this.maxScroll > 2;
      this.classList.toggle('product-carousel--static', !overflowing);

      if (this.prevButton) {
        this.prevButton.hidden = !overflowing;
        this.prevButton.disabled = !this.loop && this.atStart;
      }

      if (this.nextButton) {
        this.nextButton.hidden = !overflowing;
        this.nextButton.disabled = !this.loop && this.atEnd;
      }
    }

    startAutoplay() {
      if (!this.autoplayEnabled || this.timer) return;
      this.timer = setInterval(() => {
        if (!this.loop && this.atEnd) return this.stopAutoplay();
        this.step(1);
      }, this.autoplayDelay);
    }

    stopAutoplay() {
      clearInterval(this.timer);
      this.timer = null;
    }

    restartAutoplay() {
      this.stopAutoplay();
      this.startAutoplay();
    }
  }

  customElements.define('product-carousel', ProductCarousel);
}
